/**
 * IssuesEntryCard — T12 (E8/Q6): Einstiegshilfe auf der Tool-Seite
 * („Erstellen"-Tab), solange kein `?products=` übergeben wurde. Lädt
 * Produkte mit Regelverstößen für einen gewählten Channel (bestehender
 * `products/issues`-Endpoint, siehe `useIssuesEntryProducts`) und übergibt
 * sie als Produkt-Auswahl an den Wizard (max 50).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Select } from '@/components/v2/ui'
import { useChannels } from '@/hooks/useChannels'
import { EID } from '@/lib/element-ids.registry'

import { useIssuesEntryProducts } from './hooks/useIssuesEntryProducts'

interface IssuesEntryCardProps {
  onProductsLoaded: (productIds: string[]) => void
}

export function IssuesEntryCard({ onProductsLoaded }: IssuesEntryCardProps) {
  const { t } = useTranslation()
  const { data: channels = [] } = useChannels()
  const [channelId, setChannelId] = useState<string | undefined>(undefined)
  const { mutate, isPending, data, isSuccess } = useIssuesEntryProducts()

  const handleLoad = () => {
    if (!channelId) return
    mutate(channelId, {
      onSuccess: (ids) => {
        if (ids.length === 0) return
        toast.success(t('channelContent.wizard.issuesEntry.loaded', { count: ids.length }))
        onProductsLoaded(ids)
      },
    })
  }

  return (
    <section
      aria-label={t('channelContent.wizard.issuesEntry.title')}
      className="text-bulk-issues-entry space-y-3 rounded-lg border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900"
    >
      <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
        {t('channelContent.wizard.issuesEntry.title')}
      </h2>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        {t('channelContent.wizard.issuesEntry.description')}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Select
            id={EID['text-bulk-issues-entry-channel-dropdown']}
            label={t('channelContent.wizard.issuesEntry.channelLabel')}
            options={channels.map((c) => ({ value: c.id, label: c.custom_name || c.channel_name }))}
            value={channelId}
            onValueChange={setChannelId}
          />
        </div>
        <button
          type="button"
          id={EID['text-bulk-issues-entry-load-btn']}
          onClick={handleLoad}
          disabled={!channelId || isPending}
          className="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:border-orange-300 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-300"
        >
          {t('channelContent.wizard.issuesEntry.loadButton')}
        </button>
      </div>
      {isSuccess && data && data.length === 0 && (
        <p role="status" className="text-sm text-neutral-500 dark:text-neutral-400">
          {t('channelContent.wizard.issuesEntry.empty')}
        </p>
      )}
    </section>
  )
}
